import { CONSTANTS } from "reducers/checkpoint";
import PropTypes from "prop-types";
import React from "react";
import { Filter, FilterSetController } from "@weezevent/nacre";
import { Trans, useTranslation } from "react-i18next";

const SearchBarFilters = ({
	filters,
	onChangeFilter,
	totalCount,
	weezTagsData
}) => {
	const { t } = useTranslation();

	const changeSearch = value => onChangeFilter("search", value);

	const handleChange = React.useCallback(
		data => {
			const keyToUpdate = ["status", "created", "population_tag_id__in"];
			keyToUpdate.forEach(item => {
				if (item === "population_tag_id__in") {
					const newValue = data[item];
					// Always create a new array to ensure React detects changes
					if (newValue) {
						const tagIds = Array.isArray(newValue)
							? [...newValue]
							: newValue instanceof Set
							? Array.from(newValue)
							: [newValue];
						onChangeFilter(item, tagIds);
					} else if (filters[item]) {
						onChangeFilter(item, undefined);
					}
				} else if (data[item] !== filters[item]) {
					onChangeFilter(item, data[item]);
				}
			});
		},
		[onChangeFilter, filters]
	);

	const filtered_filters = Object.fromEntries(
		Object.entries(filters).filter(([_, value]) => value !== undefined)
	);
	const weezTagsOptions = React.useMemo(
		() =>
			weezTagsData.map(weezTag => ({
				id: String(weezTag.id),
				key: String(weezTag.id),
				text: weezTag.category_name + " - " + weezTag.period_name,
				name: String(weezTag.id)
			})),
		[weezTagsData]
	);

	return (
		<FilterSetController
			total={String(totalCount)}
			labelTotal={
				<Trans
					i18nKey={"access.checkpoint.filterset.total"}
					count={totalCount}
				/>
			}
			labelReset={t("access.common.filterset.reset")}
			labelFilters={t("access.common.filterset.filters")}
			onChange={handleChange}
			initialValues={filtered_filters}
			defaultValues={{ status: "not_archived" }}
			search={{
				onChange: (synth, value) => {
					changeSearch(value);
				},
				handleReset: () => {
					changeSearch(null);
				},
				displayResetIcon: true,
				placeholder: t("access.checkpoint.searchCheckpoint"),
				value: filters.search || ""
			}}
			onReset={() => handleChange({})}
		>
			<Filter
				name="status"
				labelName={t("access.checkpoint.filters.status")}
				options={[
					{
						key: 0,
						text: t("access.checkpoint.filters.available"),
						name: CONSTANTS.NOT_ARCHIVED
					},
					{
						key: 1,
						text: t("access.checkpoint.filters.archived"),
						name: CONSTANTS.ARCHIVED
					},
					{
						key: 2,
						text: t("access.checkpoint.filters.active"),
						name: CONSTANTS.ON
					},
					{
						key: 3,
						text: t("access.checkpoint.filters.inactive"),
						name: CONSTANTS.OFF
					}
				]}
				index="0"
			/>
			<Filter
				name="created"
				labelName={t("access.checkpoint.filters.creationDate")}
				options={[
					{
						key: 0,
						text: t("access.checkpoint.filters.sixMonths"),
						name: CONSTANTS.SIX_MONTHS
					},
					{
						key: 1,
						text: t("access.checkpoint.filters.twelveMonths"),
						name: CONSTANTS.TWELVE_MONTHS
					}
				]}
				index="1"
			/>
			{weezTagsData?.length > 0 && (
				<Filter
					name="population_tag_id__in"
					labelName={t("access.checkpoint.filters.populationTypes")}
					isSearchable
					multiple
					options={weezTagsOptions}
					index="2"
					labelSelectAll={t(
						"access.attendees.attendeesList.filters.labelSelectAll"
					)}
					labelCTA={t(
						"access.attendees.attendeesList.filters.labelCTA"
					)}
				/>
			)}
		</FilterSetController>
	);
};

SearchBarFilters.propTypes = {
	filters: PropTypes.shape({
		search: PropTypes.string.isRequired,
		created: PropTypes.string,
		status: PropTypes.string
	}).isRequired,
	onChangeFilter: PropTypes.func.isRequired,
	totalCount: PropTypes.string,
	weezTagsData: PropTypes.array
};

SearchBarFilters.defaultProps = {
	totalCount: null,
	weezTagsData: []
};

export default SearchBarFilters;
